import dayjs from 'dayjs';
import { memo, useMemo, useCallback } from 'react';

import { Grid2, Divider } from '@mui/material';

import { CustomerTrendsChart } from 'src/pages/reports/CustomerTrends/layout/CustomerTrendsChart';

import type { PromotionsSummaryLocationReport } from '../../types';

function PromotionTrendsRaw({ promotion }: { promotion: PromotionsSummaryLocationReport }) {
  const dataset = useMemo(() => {
    if (!promotion) {
      return [];
    }

    return promotion.trends.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
  }, [promotion]);

  const uniqueDates = useMemo(() => {
    const dates = new Set<string>();

    dataset.forEach((d) => {
      dates.add(d.date);
    });

    return Array.from(dates).sort((a, b) => dayjs(a).diff(dayjs(b)));
  }, [dataset]);

  const getDataset = useCallback(
    ({
      name,
      key,
    }: {
      name: string;
      key: keyof Omit<PromotionsSummaryLocationReport['trends'][0], 'date'>;
    }) => [
      {
        name,
        data: uniqueDates.map((date) =>
          dataset.filter((d) => d.date === date).reduce((acc, d) => acc + d[key], 0)
        ),
        type: 'line',
      },
    ],
    [dataset, uniqueDates]
  );

  const getDateFormatter = useCallback(() => {
    if (promotion.trendType === 'daily') {
      return (value: string) => dayjs(value).format('MMM DD');
    } else if (promotion.trendType === 'weekly') {
      return (value: string) => dayjs(value).format('MM/DD/YYYY');
    } else if (promotion.trendType === 'monthly') {
      return (value: string) => dayjs(value).format('MMM YY');
    }

    return undefined;
  }, [promotion]);

  return (
    <>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <CustomerTrendsChart
          categories={uniqueDates}
          title="Order trends during the promotion"
          subtitle="Number of orders places by new and existing customers"
          dataset={getDataset({
            name: 'Total Orders',
            key: 'totalOrders',
          })}
          dateFormatter={getDateFormatter()}
        />
      </Grid2>
      <Grid2 size={{ xs: 12, md: 6 }}>
        <CustomerTrendsChart
          categories={uniqueDates}
          title="Net sales trends during the promotion"
          subtitle="Number of orders places by new and existing customers"
          dataset={getDataset({
            name: 'Net Sales',
            key: 'totalNetSales',
          })}
          dateFormatter={getDateFormatter()}
          currency
        />
      </Grid2>
      <Grid2 size={{ xs: 12 }}>
        <Divider />
      </Grid2>
    </>
  );
}

export const PromotionTrends = memo(PromotionTrendsRaw);
